import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Process Ship and Debit Step
 *
 * Processes a Ship and Debit email using the appropriate customer-specific processor
 * to determine whether the SD was accepted or rejected.
 *
 * This step delegates to the configured processor type (from the identify step)
 * and returns the acceptance status along with any extracted metadata.
 */
export const processSDStep = defineStepConfig({
  type: "process_sd",
  name: "Process Ship and Debit",
  description:
    "Process a Ship and Debit email to determine acceptance or rejection status",
  category: "Transformation",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The Ship and Debit email to process",
    ),
    customerId: z
      .string()
      .nullish()
      .describe("Customer identifier from identify step"),
    customerName: z
      .string()
      .nullish()
      .describe("Customer name from identify step"),
    processorType: z
      .string()
      .nullish()
      .describe("Which SD processor to use for this customer"),
    customerConfig: z
      .record(z.string(), z.unknown())
      .nullish()
      .describe("Customer-specific processor configuration"),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    customerId: z
      .string()
      .nullable()
      .describe("Pass-through of customer identifier"),
    customerName: z
      .string()
      .nullable()
      .describe("Pass-through of customer name"),
    status: z
      .enum(["accepted", "rejected", "unknown", "skipped"])
      .describe("Ship and Debit acceptance status"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score of the detection (0-1)"),
    reason: z.string().describe("Explanation of the detection result"),
    metadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .describe("Additional extracted metadata"),
  }),

  configSchema: z.object({
    defaultProcessorType: z
      .string()
      .optional()
      .describe("Fallback processor type if none specified in input"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 2,
    },
  },
});
